import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo } from "react"

import type { WeekState } from "@/lib/guild-war-constants"

import { CreateWeekCard } from "@/components/guild-war-weeks/create-week-card"
import { ExistingWeeksTable } from "@/components/guild-war-weeks/existing-weeks-table"
import { ManualTransitionCard } from "@/components/guild-war-weeks/manual-transition-card"
import { WeeksIntroCard } from "@/components/guild-war-weeks/weeks-intro-card"
import { guildWarApi } from "@/lib/guild-war-api"
import { guildWarKeys } from "@/lib/query-keys"
import { createWeeksUiStore } from "@/lib/stores/weeks-ui-store"
import { useWeeksRouteStore } from "@/lib/stores/weeks-ui-store"
import {
  coerceDateLikeToString,
  formatDateInput,
  getIsoWeekId,
  getCurrentWeekWindow,
} from "@/lib/utils/week-date-utils"

type RenderableWeek = {
  id: string
  state: WeekState
  weekStartAt: string
  weekEndAt: string
  createdAt: string
  updatedAt: string
}

const normalizeWeekForRender = (week: {
  id: string
  state: WeekState
  weekStartAt: string | Date
  weekEndAt: string | Date
  createdAt: string | Date
  updatedAt: string | Date
}): RenderableWeek => ({
  ...week,
  weekStartAt: coerceDateLikeToString(week.weekStartAt),
  weekEndAt: coerceDateLikeToString(week.weekEndAt),
  createdAt: coerceDateLikeToString(week.createdAt),
  updatedAt: coerceDateLikeToString(week.updatedAt),
})

export const Route = createFileRoute("/_authed/_admin/guild-war/weeks")({
  beforeLoad: () => ({
    weeksStore: createWeeksUiStore(),
  }),
  component: WeekManagementPage,
})

function WeekManagementPage() {
  const store = useWeeksRouteStore()
  const weeksQuery = useQuery({
    queryKey: guildWarKeys.weeks(),
    queryFn: guildWarApi.listWeeks,
  })

  const weeks = useMemo(
    () => (weeksQuery.data ?? []).map(normalizeWeekForRender),
    [weeksQuery.data],
  )

  const defaults = useMemo(() => {
    const { weekStart, weekEnd } = getCurrentWeekWindow()
    const weekId = getIsoWeekId(weekStart)
    return {
      weekId,
      weekStartAt: formatDateInput(weekStart),
      weekEndAt: formatDateInput(weekEnd),
    }
  }, [])

  const signupOpenCount = weeks.filter(
    (week) => week.state === "signup_open",
  ).length
  const nextPlannedWeek = weeks[0]?.id ?? defaults.weekId

  return (
    <div className="grid gap-4">
      <WeeksIntroCard
        weeksCount={weeks.length}
        signupOpenCount={signupOpenCount}
        nextPlannedWeek={nextPlannedWeek}
        isFetching={weeksQuery.isFetching}
        onRefresh={() => void weeksQuery.refetch()}
      />

      <CreateWeekCard defaults={defaults} />

      <ExistingWeeksTable
        weeks={weeks}
        isLoading={weeksQuery.isLoading}
        isError={weeksQuery.isError}
        onRefetch={() => void weeksQuery.refetch()}
        store={store}
      />

      <ManualTransitionCard weeks={weeks} store={store} />
    </div>
  )
}
